import React, { useRef, createContext, useContext, useCallback } from 'react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import { closeModalAtom, openedModalsAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { hexToRgb } from 'src/utils/common';
export const ModalBack = styled.div<{
  isDim: boolean;
  isBlur: boolean;
  lockBodyScroll: boolean;
}>`
  z-index: 41;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  top: 0px;
  left: 0px;
  width: var(--100vw);
  height: 100%;
  max-height: 100vh;
  display: flex;
  background: ${({
  theme,
  isDim
}) => isDim ? `rgba(${hexToRgb(theme.color.surfaceDeepFixed)}, .7)` : 'transparent'};
  ${({
  isBlur
}) => isBlur && `backdrop-filter: blur(40px);`}
  ${({
  theme
}) => theme.screenSize.mobile} {
    z-index: 51;
  }
  ${({
  lockBodyScroll
}) => lockBodyScroll ? `
    position: fixed;
  ` : `
    position: absolute;
    width: 100%;
  `}
`;
export const ModalBackInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
export interface Props extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  onClose?: () => void;
  lockBodyScroll?: boolean;
  isDim?: boolean;
  isBlur?: boolean;
  closeWhenClickOutside?: boolean;
}
export const ModalKeyContext = createContext<ModalType | void>(undefined);
export const ModalKeyProvider: React.FC<React.PropsWithChildren<{
  modalType: ModalType;
}>> = ({
  modalType,
  ...props
}) => <ModalKeyContext.Provider value={modalType} data-sentry-element="ModalKeyContext.Provider" data-sentry-component="ModalKeyProvider" data-sentry-source-file="index.tsx">{props.children}</ModalKeyContext.Provider>;
export const ModalBackground: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  isDim = false,
  isBlur = false,
  className,
  onClose,
  lockBodyScroll = true,
  closeWhenClickOutside = true
}) => {
  const closeModal = useSetAtom(closeModalAtom);
  const modalType = useContext(ModalKeyContext);
  const handleClickModalOutside = useCallback(() => {
    if (closeWhenClickOutside) {
      onClose?.();
      if (modalType) {
        closeModal(modalType);
      }
    }
  }, [closeWhenClickOutside, onClose, modalType, closeModal]);
  const pointerDownTarget = useRef<EventTarget | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(rootRef, lockBodyScroll);
  return <ModalBack ref={rootRef} lockBodyScroll={lockBodyScroll} isDim={isDim} isBlur={isBlur} className={`ModalBack ${className}`} role='button' tabIndex={0} onPointerDown={e => {
    pointerDownTarget.current = e.target;
  }} onClick={e => {
    if (e.currentTarget === e.target && pointerDownTarget.current === e.target) {
      handleClickModalOutside();
    }
    pointerDownTarget.current = null;
  }} data-sentry-element="ModalBack" data-sentry-component="ModalBackground" data-sentry-source-file="index.tsx">
      <ModalBackInner role='button' tabIndex={0} data-sentry-element="ModalBackInner" data-sentry-source-file="index.tsx">
        {children}
      </ModalBackInner>
    </ModalBack>;
};
type ModalProps = { [key in ModalType]?: React.ReactElement };
const Modal: React.FC = () => {
  const openedModals = useAtomValue(openedModalsAtom);
  const [renderedModals, setRenderedModals] = React.useState<ModalProps>({});
  React.useEffect(() => {
    setRenderedModals(prevRenderedModals => {
      const nextRenderedModals: ModalProps = {};
      const preserve = openedModals.map(i => i.key);
      for (const key in prevRenderedModals) {
        if (!preserve.includes(key as ModalType)) {
          delete prevRenderedModals[key as ModalType];
        }
      }
      openedModals.map(({
        component,
        key
      }) => {
        const Component = component;
        if (prevRenderedModals[key]) {
          return;
        }
        if (typeof Component === 'boolean') {
          return;
        }
        nextRenderedModals[key] = <ModalKeyProvider modalType={key as ModalType} key={key}>
            <Component />
          </ModalKeyProvider>;
      });
      return {
        ...prevRenderedModals,
        ...nextRenderedModals
      };
    });
  }, [openedModals]);
  return <>{Object.values(renderedModals)}</>;
};
export default Modal;